import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EventService } from '@shared/services/event.service';
import { EventModel } from '@shared/models/event.model';
import { GoogleMapsDirections } from '@shared/const/google-maps/google-maps.const';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, of, Subscription, from } from 'rxjs';
import { PublicPageTitleService } from '@public/app/services/public-page-title.service';
import { AttendeesService } from '@shared/services/attendees.service';
import { AuthService } from '@shared/services/auth.service';
import { LoadingIndicatorService } from '@public/app/services/loading-indicator.service';
import { ToastService } from '@public/app/services/toast.service';
import { callableErrorMessage } from '@shared/errors/callable-error-message';

@Component({
  selector: 'app-event-details',
  templateUrl: './event-details.component.html',
  styleUrls: ['./event-details.component.scss'],
  standalone: false
})
export class EventDetailsComponent implements OnInit, OnDestroy {
  event$: Observable<EventModel | null>;
  // 'unavailable': unknown, not approved, or closed more than 24 hours ago (the upcoming lists drop it then too).
  state: 'loading' | 'ready' | 'unavailable' | 'error' = 'loading';
  subscriptions: Subscription[] = [];
  private retry$ = new BehaviorSubject<void>(undefined);
  pageTitle = '';

  showRegistrationModal = false;
  selectedEvent: EventModel | null = null;
  isRegisteredModal = false;
  userName = '';
  userEmail = '';

  constructor(
    private route: ActivatedRoute,
    private eventService: EventService,
    private publicPageTitleService: PublicPageTitleService,
    private attendeesService: AttendeesService,
    private authService: AuthService,
    private loadingIndicatorService: LoadingIndicatorService,
    private toastService: ToastService
  ) {
    this.publicPageTitleService.setPageTitle(this.pageTitle);
  }

  ngOnInit(): void {
    this.event$ = combineLatest([this.route.paramMap, this.retry$]).pipe(
      switchMap(([params]) => {
        const eventId = params.get('id');
        if (!eventId) {
          this.state = 'unavailable';
          return of(null);
        }
        this.state = 'loading';
        return from(this.eventService.getById(eventId)).pipe(
          map(event => {
            this.state = event && EventService.isUpcoming(event) ? 'ready' : 'unavailable';
            return this.state === 'ready' ? event : null;
          }),
          catchError(error => {
            console.error('Error loading event:', error);
            this.state = 'error';
            return of(null);
          })
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.subscriptions.push(
      this.event$.subscribe(ev => {
        if (ev) {
          this.publicPageTitleService.setPageTitle(ev.name);
        }
      })
    );
  }

  reload(): void {
    this.retry$.next();
  }

  mapsUrl(event: EventModel): string | null {
    const destination = [event.city, event.address]
      .map(part => (part || '').trim())
      .filter(Boolean)
      .join(GoogleMapsDirections.VENUE_SEPARATOR);
    if (!destination) {
      return null;
    }
    const query = `api=${GoogleMapsDirections.API_VERSION}&destination=${encodeURIComponent(destination)}`;
    return `${GoogleMapsDirections.URL}?${query}`;
  }

  openRegistrationModal(event: EventModel): void {
    this.selectedEvent = event;
    this.isRegisteredModal = !!event.isRegistered;
    this.showRegistrationModal = true;

    const currentUser = this.authService.getCurrentUser();
    this.userName = currentUser ? currentUser.name : 'Ismeretlen felhasználó';
    this.userEmail = currentUser ? currentUser.email : 'Ismeretlen felhasználó';
  }

  cancelModal(): void {
    this.showRegistrationModal = false;
    this.selectedEvent = null;
    this.isRegisteredModal = false;
  }

  confirmRegistration(): void {
    this.loadingIndicatorService.showLoading();
    if (!this.selectedEvent || !this.authService.getCurrentUser()) {
      console.log('Missing feature: handle no user or event');
      this.loadingIndicatorService.hideLoading();
      return;
    }
    const event = this.selectedEvent;
    this.attendeesService
      .goToEvent(this.authService.getCurrentUser().id, event.id, event.organiserId, this.userName)
      .subscribe({
        next: result => {
          event.isRegistered = true;
          event.numberOfAttendees = result?.numberOfAttendees ?? event.numberOfAttendees + 1;
          this.toastService.showSuccess('Sikeres jelentkezés');
        },
        error: err => {
          const errorMsg = callableErrorMessage(err);
          this.loadingIndicatorService.hideLoading();
          this.toastService.showError(errorMsg);
        },
        complete: () => {
          this.loadingIndicatorService.hideLoading();
        },
      });
  }

  onUnregister(event: EventModel): void {
    this.loadingIndicatorService.showLoading();
    if (!this.authService.getCurrentUser()) {
      console.log('Missing feature: handle no user');
      this.loadingIndicatorService.hideLoading();
      return;
    }
    this.attendeesService
      .cancelApplicationToEvent(this.authService.getCurrentUser().id, event.id, event.organiserId)
      .subscribe({
        next: result => {
          event.isRegistered = false;
          event.numberOfAttendees = result?.numberOfAttendees ?? event.numberOfAttendees - 1;
          this.toastService.showSuccess('Eseményről való lejelentkezés sikeres');
        },
        error: err => {
          const errorMsg = callableErrorMessage(err);
          this.loadingIndicatorService.hideLoading();
          this.toastService.showError(errorMsg);
        },
        complete: () => {
          this.loadingIndicatorService.hideLoading();
        },
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
